import React from 'react';
import {
  Body,
  Tooltip,
  designSystemToken,
} from '@lightricks/react-design-system';
import {formatDueDate} from '@/utils/formattedDate';
import {
  ContentSubmission,
  ContentSubmissionState,
  ContentSubmissionVersion,
} from '@/types/contentSubmission';
import {getApprovalStepDisplayName} from '@/views/campaigns/constants/approvalSteps';
import ContentSubmissionTag, {
  mapStateToTagVariant,
} from '@/components/content-submission-tag';
import ContentSubmissionVersionSelector from '../content-submission-version-selector';
import styles from './ContentSubmissionSubHeader.module.scss';

export interface ContentSubmissionSubHeaderProps {
  testID?: string;
  contentSubmission: ContentSubmission;
  activeVersionId: string | null;
  onVersionChange?: (versionId: string) => void;
  /** When provided, shown as a plain label instead of the version selector. */
  contentTypeLabelOverride?: string;
}

function computeApprovalDepartments(
  contentSubmission: ContentSubmission,
  activeVersion: ContentSubmissionVersion | undefined
): {pendingDepartments: string[]; approvedStepsNames: string[]} {
  const requiredSteps =
    contentSubmission.campaign?.campaignApprovalSteps?.map((s) => s.step) ?? [];
  const approvedStepKeys = (activeVersion?.approvals ?? []).map((a) => a.step);
  return {
    pendingDepartments: requiredSteps
      .filter((s) => !approvedStepKeys.includes(s))
      .map((s) => getApprovalStepDisplayName(s)),
    approvedStepsNames: requiredSteps
      .filter((s) => approvedStepKeys.includes(s))
      .map((s) => getApprovalStepDisplayName(s)),
  };
}

function humanizeKind(kind: string): string {
  return kind.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function ContentSubmissionSubHeader({
  testID = 'content-submission-sub-header',
  contentSubmission,
  activeVersionId,
  onVersionChange,
  contentTypeLabelOverride,
}: ContentSubmissionSubHeaderProps) {
  const {versions, profile, deliverableKind, kind, dueSoon, dueAt} =
    contentSubmission;
  const contentTypeLabel = humanizeKind(deliverableKind ?? kind);
  const activeVersion = versions?.find(
    (version) => version.id === activeVersionId
  );
  const {pendingDepartments, approvedStepsNames} = computeApprovalDepartments(
    contentSubmission,
    activeVersion
  );

  const dueDate = formatDueDate(dueAt);

  const hasVersionSelector =
    !contentTypeLabelOverride && versions && versions.length > 0;
  const showDot =
    profile?.displayName && (hasVersionSelector || contentTypeLabelOverride);

  return (
    <div className={styles.container} data-testid={testID}>
      {profile?.displayName ? (
        <Body
          size="lg"
          color={designSystemToken('semantic.fg.primary')}
          className={styles.creatorName}
        >
          {profile.displayName}
        </Body>
      ) : null}
      {showDot ? (
        <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
          ·
        </Body>
      ) : null}
      {contentTypeLabelOverride ? (
        <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
          {contentTypeLabelOverride}
        </Body>
      ) : null}
      {hasVersionSelector && onVersionChange ? (
        <ContentSubmissionVersionSelector
          versions={versions ?? []}
          activeVersionId={activeVersionId}
          onVersionChange={onVersionChange}
          contentTypeLabel={contentTypeLabel}
        />
      ) : null}
      <ContentSubmissionTag
        variant={mapStateToTagVariant(
          contentSubmission.aasmState as ContentSubmissionState,
          pendingDepartments,
          approvedStepsNames.length,
          contentSubmission.versionCount
        )}
        departments={pendingDepartments}
      />
      {dueSoon ? (
        <Tooltip title={dueDate} placement="top" type="dark" arrow>
          <div>
            <ContentSubmissionTag variant="overdue" />
          </div>
        </Tooltip>
      ) : null}
    </div>
  );
}

export default ContentSubmissionSubHeader;
